import { eventTarget } from '@cornerstonejs/core';
import {
  Enums as csToolsEnums,
  utilities as csToolsUtilities,
} from '@cornerstonejs/tools';
import { utils as gleamerUtils } from '@gleamer/ui';
import { CommandsManager, ServicesManager } from '@ohif/core';
import { Slider, ToolButton } from '@ohif/ui-next';
import React, { useCallback, useEffect, useState } from 'react';
import {
  BRUSH_EVENTS,
  BRUSH_SLIDER_MAX,
  BRUSH_TOOL_NAME,
  brushSizeToSliderValue,
  DEFAULT_BRUSH_SIZE,
  ERASER_TOOL_NAME,
  getActiveBrushToolName,
  sliderValueToBrushSize,
} from '../../../utils/tools/brush';

type BrushToolbarControlsProps = {
  id: string;
  servicesManager: ServicesManager;
  commandsManager: CommandsManager;
};

/**
 * Brush, Eraser and brush size, shown while a task's observation uses the Brush
 * and always in the bac mode. The buttons only run commands; the
 * state shown here is read back from the active viewport's tool group.
 */
export function BrushToolbarControls({
  id,
  servicesManager,
  commandsManager,
}: BrushToolbarControlsProps) {
  const { viewportGridService, toolGroupService } = servicesManager.services;
  const [activeToolName, setActiveToolName] = useState<string | null>(null);
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH_SIZE);

  const isBacMode = gleamerUtils.getMode() === gleamerUtils.BAC_MODE_NAME;

  const refresh = useCallback(() => {
    const { activeViewportId } = viewportGridService.getState();
    const toolGroup = activeViewportId
      ? toolGroupService.getToolGroupForViewport(activeViewportId)
      : null;
    setActiveToolName(getActiveBrushToolName(toolGroup));
    if (toolGroup) {
      setBrushSize(
        csToolsUtilities.segmentation.getBrushSizeForToolGroup(toolGroup.id) ??
          DEFAULT_BRUSH_SIZE
      );
    }
  }, [viewportGridService, toolGroupService]);

  useEffect(() => {
    refresh();
    const toolEvents = [
      csToolsEnums.Events.TOOL_ACTIVATED,
      csToolsEnums.Events.TOOL_MODE_CHANGED,
    ];
    toolEvents.forEach(event => eventTarget.addEventListener(event, refresh));
    window.addEventListener(BRUSH_EVENTS.SIZE_CHANGED, refresh);
    const { unsubscribe } = viewportGridService.subscribe(
      viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
      refresh
    );
    return () => {
      toolEvents.forEach(event =>
        eventTarget.removeEventListener(event, refresh)
      );
      window.removeEventListener(BRUSH_EVENTS.SIZE_CHANGED, refresh);
      unsubscribe();
    };
  }, [refresh, viewportGridService]);

  if (!isBacMode && !activeToolName) {
    return null;
  }

  const setTool = (toolName: string) =>
    commandsManager.runCommand('setGleamerBrushTool', { toolName });

  return (
    <div
      key={id}
      className="flex items-center gap-1 text-white"
      data-cy="brush-controls"
    >
      <ToolButton
        id="Brush"
        icon="icon-tool-brush"
        label="Brush"
        tooltip="Brush (B to switch to the eraser)"
        isActive={activeToolName === BRUSH_TOOL_NAME}
        onInteraction={() => setTool(BRUSH_TOOL_NAME)}
      />
      <ToolButton
        id="Eraser"
        icon="icon-tool-eraser"
        label="Eraser"
        tooltip="Eraser (B to switch to the brush)"
        isActive={activeToolName === ERASER_TOOL_NAME}
        onInteraction={() => setTool(ERASER_TOOL_NAME)}
      />
      <div className="flex w-28 flex-col px-1" title="Brush size (+ / -)">
        <Slider
          aria-label="Brush size"
          min={0}
          max={BRUSH_SLIDER_MAX}
          step={1}
          value={[brushSizeToSliderValue(brushSize)]}
          onValueChange={([value]) =>
            commandsManager.runCommand('setGleamerBrushSize', {
              value: sliderValueToBrushSize(value),
            })
          }
        />
        <span className="text-muted-foreground mt-1 text-center text-xs">
          {brushSize.toFixed(2)} mm
        </span>
      </div>
    </div>
  );
}
